
export enum Collection {
  All = 'All',
  WinterEditII = 'Winter Edit II',
  WinterFormals = 'Winter Formals',
  LawnVol2_2025 = 'Lawn Vol 2 2025'
}

export type UserRole = 'Admin' | 'Cashier' | 'Warehouse Manager' | 'Viewer';

export interface ProductSize {
  sku: string;
  size: string;        // e.g. "1"
  stock: number;       // store stock (mapped from backend storeStock)
  warehouseStock: number;
  // Backend UUIDs required for POS / stock-movement calls
  productStockId?: string;
  stockId?: string;
  barcode?: string;
}

export interface Product {
  _id: string;
  id: string;          // productId UUID
  name: string;        // productName
  collection: string;  // collection name (resolved from collectionId)
  collectionId?: string; // raw UUID — kept for edits
  price: number;       // decimal returned as string by backend; always parseFloat before storing
  image?: string;
  description?: string;
  material?: string;
  stocks: ProductSize[];
  createdAt?: string;
  brand?: string;
}

export interface CartItem extends Product {
  selectedSize: ProductSize;
  quantity: number;
}

export interface Customer {
  _id: string;         // customerId UUID
  id?: string;         // alias
  name: string;        // customerName
  phone: string;       // customerPhone
  email: string;       // customerEmail
  loyaltyPoints: number;
  totalSpent: number;  // decimal string from backend → number
}

export interface CashierUser {
  _id?: string;        // userId UUID (from GET /user list)
  id?: string;         // userId UUID (from auth response)
  username: string;    // userName / username
  name?: string;       // display alias — same as username since backend has no separate name
  email: string;
  password?: string;   // only used locally, never sent to backend
  role: UserRole;
  phoneNumber?: string;
}

export interface Collections {
  _id: string;         // collectionId UUID
  id: string;          // collectionId UUID
  name: string;        // collectionName
}

// Values MUST match the backend movementType enum exactly
export enum TransactionType {
  Sale = 'Sale',
  Return = 'Return',                        // frontend-only (isReturned flag on backend)
  Shopify = 'Shopify',
  PreOrder = 'Pre-order',                   // was 'Pre Order'
  PR = 'PR and Gift',                       // was 'PR'
  WarehouseTransfer = 'Store to Warehouse', // was 'WarehouseTransfer'
  FnF = 'Friends and Family',               // was 'FnF'
  Transfer = 'Warehouse to Store',          // was 'Transfer'
}

// Backend movementType enum (stock_movement table) — also used in transaction.type
export const BACKEND_MOVEMENT_TYPES = [
  'Sale',
  'PR and Gift',
  'Friends and Family',
  'Warehouse to Store',
  'Store to Warehouse',
  'Shopify',
  'Pre-order',
] as const;
export type BackendMovementType = typeof BACKEND_MOVEMENT_TYPES[number];

// Map any frontend type to a valid backend movement enum value
export function toBackendMovementType(t: string): BackendMovementType {
  const map: Record<string, BackendMovementType> = {
    'Sale': 'Sale',
    'Return': 'Store to Warehouse',     // stock goes back to warehouse on return
    'Shopify': 'Shopify',
    'Pre Order': 'Pre-order',
    'Pre-order': 'Pre-order',
    'PR': 'PR and Gift',
    'PR and Gift': 'PR and Gift',
    'WarehouseTransfer': 'Store to Warehouse',
    'Store to Warehouse': 'Store to Warehouse',
    'FnF': 'Friends and Family',
    'Friends and Family': 'Friends and Family',
    'Transfer': 'Warehouse to Store',
    'Warehouse to Store': 'Warehouse to Store',
    // Inward / Outward used by stock track — map to closest
    'Inward': 'Warehouse to Store',
    'Outward': 'Store to Warehouse',
    'Adjustment': 'Warehouse to Store',
  };
  return map[t] ?? 'Sale';
}

export enum StockMovementType {
  Sale = 'Sale',
  Return = 'Return',
  Adjustment = 'Adjustment',
  Transfer = 'Transfer',
  Inward = 'Inward',
  Outward = 'Outward'
}

export interface StockMovement {
  id: string;
  movementId?: string;   // backend UUID
  timestamp: string;
  productId: string;
  productName: string;
  size: string;
  stockId?: string;      // backend UUID (productSize column)
  type: TransactionType | StockMovementType | string;
  movementType?: string; // raw backend field
  location: 'Store' | 'Warehouse' | 'Both';
  quantityChange: number;
  newStoreStock: number;
  newWhStock: number;
  updatedStoreStock?: number;
  updatedWarehouseStock?: number;
  performedBy: string;
  notes?: string;
}

export interface Transaction {
  id: string;             // frontend display ID (TXN-xxx or transactionId UUID)
  transactionId?: string; // backend UUID
  date: Date;
  type: TransactionType | string;
  items: CartItem[];      // may be empty when loaded from flat backend rows
  subtotal: number;
  total: number;
  paymentMethod: 'Cash' | 'Card' | 'Bank Transfer' | 'N/A';
  referenceNumber?: string;
  transferReferenceNumber?: string; // backend field name
  amountPaid: number;
  balance: number;
  isPartial: boolean;
  cashReceived?: number;
  changeAmount?: number;
  customer?: Customer;
  customerId?: string;    // raw backend UUID
  shift: 'Morning' | 'Night' | 'Day' | 'N/A';
  shiftType?: 'Day' | 'Night'; // raw backend field
  businessDate: string;
  cashierName: string;
  cashierRole?: UserRole;
  locationSource?: string;
  locationDestination?: string;
  orderDiscount?: number;
  externalOrderId?: string;
  recipientName?: string;
  notes?: string;
  originalTransactionId?: string;
  isReturned: boolean;
  isExchanged: boolean;
  // Single-item fields (flat backend rows)
  productStockId?: string;
  quantity?: number;
}

export interface AuditLog {
  id: string;
  logId?: string;   // backend UUID
  timestamp: string;
  user: string;
  action: string;
  details: string;
}

export interface SessionInfo {
  startTime: Date;
  shift: 'Morning' | 'Night' | 'Day';
  businessDate: Date;
  name: string;
  locationType: 'retail' | 'popup';
  cashierName?: string;
}

export interface ShiftRecord {
  id: string;
  shiftId?: string;
  name: string;
  cashierId?: string;
  cashierName?: string;
  startTime: Date;
  endTime: Date;
  shift: 'Morning' | 'Night' | 'Day';
  shiftType?: 'Day' | 'Night';
  locationType: 'retail' | 'popup';
  location?: 'Store' | 'Warehouse';
  businessDate: string;
  totalSales: number;
  cashSales: number;
  cardSales: number;
  bankTransferSales?: number;
  transactionCount: number;
}

export enum ViewState {
  POS = 'POS',
  Transactions = 'Transactions',
  Returns = 'Returns',
  Exchange = 'Exchange',
  WarehouseTransfer = 'WarehouseTransfer',
  StockTrack = 'StockTrack',
  Inventory = 'Inventory',
  InventoryOut = 'InventoryOut',
  StockReport = 'StockReport',
  Customers = 'Customers',
  Collections = 'Collections',
  CashierManagement = 'CashierManagement',
  Reports = 'Reports',
  AuditLogs = 'AuditLogs',
  Backup = 'Backup',
  Barcodes = 'Barcodes'
}

export interface ReturnRecord {
  returnId: string;
  transactionId: string;
  productStockId: string;
  quantity: number;
  refundAmount: number;
  reason: string | null;
  cashierName: string;
  notes: string | null;
  createdAt: string;
  // joined from original transaction
  originalType: string;
  originalTotal: number;
  originalPaymentMethod: string;
  originalCustomerId: string | null;
  originalShiftType: string;
  originalCashierName: string;
  originalCreatedAt: string;
}

export interface ChatMessage {
  role: 'user' | 'model';
  text: string;
}

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message: string;
}
